import { useSyncExternalStore } from "react";
import { fetchNotifications, fetchUnreadCount, markNotificationsRead } from "./notificationsApi";
import type { AppNotification } from "../types/notifications";
import { getUserFromLocalStorage } from "../utils/utils";

/**
 * Single source of truth for the in-app notification inbox (transactional notifications + admin announcements).
 * Everything that shows or changes notifications - header bell, dashboard announcement card, push tap handlers -
 * reads and writes THIS store, so the badge, the list and every card can never disagree.
 * Plain module store (no Redux changes needed); React reads it through useSyncExternalStore.
 */

/** Fired by usePushNotifications when a push arrives while the tab is open, so the badge updates instantly. */
export const PUSH_RECEIVED_EVENT = "wolfpack:push-received";

export type NotificationFilter = "all" | "unread";

export interface NotificationState {
  /** Server-authoritative unread count (transactional + announcements). */
  unread: number;
  items: AppNotification[];
  nextBefore: string | null;
  loading: boolean;
  /** True once the first page for the current filter has arrived. */
  loaded: boolean;
  filter: NotificationFilter;
  /** Bumped whenever the list was refreshed from the server. */
  feedVersion: number;
  /** True once the first server count has arrived. Lets the bell animate on NEW notifications, not on the first load. */
  countReady: boolean;
}

const POLL_MS = 60_000;
const PAGE = 20;
const FEED_ID = /^(campaign:)?[a-f\d]{24}$/i;

const initial = (): NotificationState => ({ unread: 0, items: [], nextBefore: null, loading: false, loaded: false, filter: "all", feedVersion: 0, countReady: false });

let state: NotificationState = initial();
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());
const set = (patch: Partial<NotificationState>) => {
  state = { ...state, ...patch, ...("unread" in patch ? { countReady: true } : {}) };
  emit();
};

// Guards against out-of-order responses (e.g. user flips All/Unread quickly) and against logout/login in one tab.
let generation = 0;
let owner: string | null = null;

export const getNotificationState = (): NotificationState => state;

export const subscribeNotificationState = (l: () => void) => {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
};

export function useNotificationState(): NotificationState {
  return useSyncExternalStore(subscribeNotificationState, getNotificationState, getNotificationState);
}

export const resetNotificationState = () => {
  generation += 1;
  state = initial();
  emit();
};

/** Kept for backwards compatibility with callers that only know the old store. */
export const setUnread = (n: number) => {
  const v = Math.max(0, Number(n) || 0);
  if (v !== state.unread || !state.countReady) set({ unread: v });
};
export const bumpFeedVersion = () => {
  void refreshNotifications();
};

const now = () => new Date().toISOString();

/** Re-read just the badge number (cheap). */
export async function refreshCount(): Promise<void> {
  const gen = generation;
  const n = await fetchUnreadCount();
  if (gen === generation && n !== null) setUnread(n);
}

/** First page for the current filter. */
export async function loadNotifications(): Promise<void> {
  const gen = generation;
  set({ loading: true });
  const feed = await fetchNotifications({ limit: PAGE, unreadOnly: state.filter === "unread" });
  if (gen !== generation) return;
  if (feed) set({ items: feed.items, nextBefore: feed.nextBefore, unread: feed.unread, loaded: true, loading: false, feedVersion: state.feedVersion + 1 });
  else set({ loading: false });
}

/**
 * Silent refresh after a push / focus: replace the newest page with fresh data, but keep older pages the user already
 * scrolled to. No spinner, no flicker.
 */
export async function refreshNotifications(): Promise<void> {
  if (!state.loaded) {
    await refreshCount();
    return;
  }
  const gen = generation;
  const feed = await fetchNotifications({ limit: PAGE, unreadOnly: state.filter === "unread" });
  if (gen !== generation || !feed) return;
  const fresh = feed.items;
  const ids = new Set(fresh.map((i) => i.id));
  const oldest = fresh.length ? new Date(fresh[fresh.length - 1].createdAt).getTime() : 0;
  const older = feed.nextBefore ? state.items.filter((i) => !ids.has(i.id) && new Date(i.createdAt).getTime() < oldest) : [];
  set({
    items: fresh.concat(older),
    nextBefore: older.length ? state.nextBefore : feed.nextBefore,
    unread: feed.unread,
    feedVersion: state.feedVersion + 1,
  });
}

export async function loadMoreNotifications(): Promise<void> {
  if (!state.nextBefore || state.loading) return;
  const gen = generation;
  set({ loading: true });
  const feed = await fetchNotifications({ limit: PAGE, before: state.nextBefore, unreadOnly: state.filter === "unread" });
  if (gen !== generation) return;
  if (!feed) {
    set({ loading: false });
    return;
  }
  const seen = new Set(state.items.map((i) => i.id));
  set({ items: state.items.concat(feed.items.filter((i) => !seen.has(i.id))), nextBefore: feed.nextBefore, unread: feed.unread, loading: false });
}

export async function setNotificationFilter(filter: NotificationFilter): Promise<void> {
  if (filter === state.filter) return;
  generation += 1;
  set({ filter, items: [], nextBefore: null, loaded: false, loading: false });
  await loadNotifications();
}

/**
 * Optimistic mark-read for any mix of transactional ids and "campaign:<id>" ids, reconciled with the server count.
 * Items already read are ignored, so the badge never under-counts.
 */
export async function markNotificationsAsRead(ids: string[]): Promise<void> {
  const valid = Array.from(new Set(ids.filter((id) => FEED_ID.test(id))));
  if (!valid.length) return;
  const want = new Set(valid);
  const flipped = state.items.filter((i) => want.has(i.id) && i.read === false).length;
  const stamp = now();
  set({
    items: state.items.map((i) => (want.has(i.id) && i.read === false ? { ...i, read: true, readAt: stamp } : i)),
    unread: Math.max(0, state.unread - flipped),
  });
  const gen = generation;
  const res = await markNotificationsRead({ ids: valid });
  if (gen !== generation) return;
  if (res) setUnread(res.unread);
  else await refreshCount();
}

export async function markAllNotificationsAsRead(): Promise<void> {
  const stamp = now();
  set({ items: state.items.map((i) => (i.read === false ? { ...i, read: true, readAt: stamp } : i)), unread: 0 });
  const gen = generation;
  const res = await markNotificationsRead({ all: true });
  if (gen !== generation) return;
  if (res) setUnread(res.unread);
  else await refreshCount();
}

/* ------------------------------ background sync ----------------------------- */

const tokenOf = (): string | null => {
  try {
    return getUserFromLocalStorage()?.userToken || null;
  } catch {
    return null;
  }
};

let consumers = 0;
let stopSync: (() => void) | null = null;

const startSync = () => {
  const tick = () => {
    const token = tokenOf();
    if (!token) {
      if (owner !== null) {
        owner = null;
        resetNotificationState();
      }
      return;
    }
    if (owner !== null && owner !== token) resetNotificationState(); // different account in the same tab
    owner = token;
    if (document.visibilityState !== "visible") return;
    // Foreground refresh also keeps an open list current, not only the badge.
    void (state.loaded ? refreshNotifications() : refreshCount());
  };
  const onPush = () => {
    if (tokenOf()) void refreshNotifications();
  };
  tick();
  const timer = window.setInterval(tick, POLL_MS);
  window.addEventListener("focus", tick);
  window.addEventListener("online", tick);
  document.addEventListener("visibilitychange", tick);
  window.addEventListener(PUSH_RECEIVED_EVENT, onPush);
  return () => {
    window.clearInterval(timer);
    window.removeEventListener("focus", tick);
    window.removeEventListener("online", tick);
    document.removeEventListener("visibilitychange", tick);
    window.removeEventListener(PUSH_RECEIVED_EVENT, onPush);
  };
};

/**
 * Reference-counted background sync (badge poll every 60 s while the tab is visible, plus focus / online / push).
 * Any number of components may call it; only one poller ever runs.
 */
export function acquireNotificationSync(): () => void {
  consumers += 1;
  if (consumers === 1) stopSync = startSync() || null;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    consumers -= 1;
    if (consumers === 0 && stopSync) {
      stopSync();
      stopSync = null;
    }
  };
}
